import { WebMercatorViewport } from "@deck.gl/core";
import {
  area as turfArea,
  distance,
  Feature,
  featureCollection,
  FeatureCollection,
  kinks,
  length as turfLength,
  lineString,
  mask as turfMask,
  multiPolygon,
  nearestPoint,
  Point,
  point,
  polygon,
  MultiPolygon,
  LineString,
  lineChunk,
  buffer,
  Polygon,
  Geometry,
  cleanCoords,
  simplify,
  difference,
  area,
} from "@turf/turf";
import circleToPolygon from "circle-to-polygon";
import colorString from "color-string";
import { isEmpty, isEqual, last, round, trim } from "lodash";

import { defaultShapeColor } from "components/map/config";

import { turfAlongWithTime } from "utils/turf-utils";

type RGBAColor = [number, number, number, number?];

const getTotalArea = (feature: Feature) => {
  if (feature?.geometry?.type === "MultiPolygon") {
    return turfArea(multiPolygon(feature?.geometry?.coordinates as any));
  }

  if (feature?.geometry?.type === "Polygon") {
    return turfArea(polygon(feature?.geometry?.coordinates as any));
  }

  return 0;
};

const getFeaturesTotalArea = (features?: Feature[]) => {
  return features?.reduce((acc, feature) => {
    return acc + getTotalArea(feature);
  }, 0);
};

const diffAreaObject = (updated: any, initial: any) => {
  const diff: any = {};

  Object.keys(updated).forEach((key) => {
    if (!isEqual(updated[key], initial[key])) {
      diff[key] = updated[key];
    }
  });

  return diff;
};

// Function to combine multiple GeoJSON features and Feature Collections into a single MultiPolygon
const combineFeaturesIntoMultiPolygon = (features: any) => {
  const polygons: any = [];

  // Extract polygons from GeoJSON features and merge them into one array
  const extractor = (data: any) => {
    data?.forEach((feature: any) => {
      // convert circle to polygons
      if (feature?.geometry && feature?.properties?.radius) {
        const c = circleToPolygon(feature?.geometry?.coordinates, feature.properties.radius, 24);
        polygons.push(c.coordinates);
      }
      if (feature?.geometry && feature?.geometry?.type === "Polygon") {
        polygons.push(feature.geometry.coordinates);
      }
      if (feature?.geometry && feature?.geometry?.type === "MultiPolygon") {
        polygons.push(...feature.geometry.coordinates);
      }
    });
  };

  extractor(features);

  return multiPolygon(polygons);
};

const filterAreasListByGroupId = (areas: any[], groupsId: string[]) => {
  return areas?.filter((area: any) => {
    if (isEmpty(groupsId)) {
      return true;
    }

    return groupsId.some((id: string) => {
      return area?.properties?.area_group_ids.includes(id);
    });
  });
};

const filterAreasListByName = (areas: any[] = [], search: string) => {
  return areas?.filter((area: any) => {
    if (!search) {
      return true;
    }
    return area?.properties?.name_display.toLowerCase().includes(trim(search)?.toLowerCase());
  });
};

const buildAreasUrl = (basePath: string | undefined, areaId: string) => {
  if (!areaId) {
    return null;
  }
  return `${basePath}areas/${areaId}/`;
};

const getLayerColor = (layerColor?: string) => {
  let color: RGBAColor = colorString.get.rgb(layerColor as string);

  if (isEmpty(color)) {
    return defaultShapeColor.RGBA as RGBAColor;
  }

  color[color.length - 1] = Number(last(color)) * 255;

  return color;
};

const getLayerBorderColor = (borderColor: string) => {
  let color: RGBAColor = colorString.get.rgb(borderColor);

  if (isEmpty(color)) {
    return defaultShapeColor.RGBA as RGBAColor;
  }

  color?.pop();

  return color;
};

const createPolygonWithMask = (polygon: Feature<any>, mask: Feature<any>) => {
  return turfMask(polygon, mask);
};

const selfIntersectingFeatureCollection = (featureCollection: FeatureCollection) => {
  let intersect = false;

  featureCollection.features.forEach((feature: any) => {
    const isPoint = feature?.geometry?.type === "Point";
    if (!isPoint && !isEmpty(kinks(feature)?.features)) {
      intersect = true;
    }
  });

  return intersect;
};

const getBBoxFromZoomAndCenter = (longitude: number, latitude: number, zoom?: number) => {
  const viewport = new WebMercatorViewport({
    width: window.innerWidth,
    height: window.innerHeight,
    longitude,
    latitude,
    zoom: zoom,
  });
  //@ts-ignore
  return viewport?.getBounds()?.map((p) => round(p, 6)) as [number, number, number, number];
};

const getBBoxFromPoints = (points: [number, number][]): any => {
  const n = points.length;
  if (n === 0) {
    return [];
  }

  const d = points[0].length;
  const lo = points[0].slice();
  const hi = points[0].slice();

  for (let i = 1; i < n; ++i) {
    const p = points[i];

    for (let j = 0; j < d; ++j) {
      const x = p[j];
      lo[j] = Math.min(lo[j], x);
      hi[j] = Math.max(hi[j], x);
    }
  }

  return [lo, hi].flat();
};

const getInterpolatedTimeFromLines = (coordinate: [number, number], lines: [number, number][], properties: any[]) => {
  if (isEmpty(coordinate) || isEmpty(lines) || !Array.isArray(coordinate) || coordinate.length !== 2) {
    return undefined;
  }

  const targetPoint = point(coordinate);
  const points = featureCollection(
    lines
      .map((pt, index) => {
        return point(pt, properties?.[index]);
      })
      .filter(Boolean)
  );

  const np = nearestPoint(targetPoint, points);
  const distanceToPoint = np.properties.distanceToPoint;
  const from = np.geometry.coordinates;
  const to = np.properties.endCoords;
  const time = np.properties.time;
  const endTime = np.properties.endTime;

  const totalDistance = distance(from, to);
  const ratio = distanceToPoint / totalDistance;
  const totalDurationInSeconds = time - endTime;
  const duration = ratio * totalDurationInSeconds;

  // Determine if the nearest point is before or after the clicked point
  const nearestPointVector = [to[0] - from[0], to[1] - from[1]];
  const clickedPointVector = [coordinate[0] - from[0], coordinate[1] - from[1]];

  /* projectionOnSegment depicts if clicked point is projected onto the positive or negative side of the segment,
  which determines if we should move forward or backward in time from the start point. */
  const projectionOnSegment =
    nearestPointVector[0] * clickedPointVector[0] + nearestPointVector[1] * clickedPointVector[1];

  if (projectionOnSegment >= 0) {
    return time - duration;
  }

  return time + duration;
};

const resamplePath = (
  coordinates?: [number, number][],
  timing: number[] = [],
  segmentLengthInKm?: number
): any[] | undefined => {
  if (!coordinates || !segmentLengthInKm) {
    return undefined;
  }

  const line = lineString(coordinates);
  const totalLength = turfLength(line);

  const numPoints = Math.ceil(totalLength / segmentLengthInKm);
  const newPaths: Point[] = [];

  // Interpolate points along the line
  for (let i = 0; i <= numPoints; i++) {
    const distance = (i + 0.5) * segmentLengthInKm; // review
    if (distance <= totalLength) {
      const point = turfAlongWithTime(line, distance, timing, { units: "kilometers" });
      newPaths.push(point as any);
    }
  }

  return newPaths;
};

/**
 * Validates if a given string is a valid latitude and longitude pair.
 *
 * @param {string} latlng - The string in the format "latitude, longitude".
 * @returns {boolean} True if the string is a valid lat/lng pair, false otherwise.
 */
const isValidLatLng = (latLng: string) => {
  // Split the string by the comma
  const parts = latLng.split(",");

  // Check if we have exactly two parts
  if (parts.length !== 2) return false;

  // Parse each part into a number
  const lat = parseFloat(parts[0].trim());
  const lng = parseFloat(parts[1].trim());

  // Validate the numeric range
  if (isNaN(lat) || isNaN(lng)) return false;

  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};

/**
 * Simplifies a polygon geometry while ensuring the number of coordinates does not exceed a max limit.
 *
 * @param inputPolygon Feature<Polygon> The original polygon to simplify.
 * @param maxPoints number Maximum number of coordinates allowed (default: 50).
 * @param toleranceRange [min, max] Range of tolerance values to try (default: [0.001, 0.05]).
 * @returns Feature<Polygon> | null Simplified polygon or null if it could not be simplified within the limit.
 */
const simplifyToMaxPoints = (
  inputPolygon: Feature<Polygon>,
  maxPoints: number = 50,
  toleranceRange: [number, number] = [0.001, 0.05]
): Feature<Polygon> | null => {
  const [minTolerance, maxTolerance] = toleranceRange;

  let low = minTolerance;
  let high = maxTolerance;
  let best: Feature<Polygon> | null = null;

  // Binary search for the best tolerance
  for (let i = 0; i < 10; i++) {
    // limit to 10 iterations for performance
    const mid = (low + high) / 2;
    const simplified = simplify(inputPolygon, { tolerance: mid }) as Feature<Polygon>;
    const cleaned = cleanCoords(simplified) as Feature<Polygon>;

    const coordCount = cleaned.geometry.coordinates[0].length;

    if (coordCount <= maxPoints) {
      best = cleaned;
      high = mid; // try simplifying more (smaller tolerance)
    } else {
      low = mid; // too complex, increase tolerance
    }
  }

  return best;
};

/**
 * Ensures the input is a Polygon. If it's a MultiPolygon, returns the largest Polygon.
 * Returns null if not a valid Polygon or MultiPolygon.
 *
 * @param input Feature<Polygon | MultiPolygon> | null
 * @returns Feature<Polygon> | null
 */
export const ensurePolygon = (input: Feature<Polygon | MultiPolygon> | null): Feature<Polygon> | null => {
  if (!input) return null;

  if (input.geometry.type === "Polygon") {
    return input as Feature<Polygon>;
  }

  if (input.geometry.type === "MultiPolygon") {
    const polygons = input.geometry.coordinates.map((coords) => polygon(coords, input.properties));
    const largest = polygons.reduce((a, b) => (area(a) > area(b) ? a : b));
    return largest as Feature<Polygon>;
  }

  return null;
};

/**
 * Converts a MultiLineString path into buffered segments.
 * Returns each buffered segment as its own polygon feature.
 *
 * @param geoJsonPaths GeoJSON object of type FeatureCollection
 * @param segmentLengthInKm Length of each chunk in kilometers (default 30 km)
 * @param bufferInMeters Radius around path in meters (default 200 m)
 * @returns FeatureCollection<Polygon> Each buffer as a separate polygon feature
 */
const pathToBufferedPolygon = (
  geoJsonPaths: FeatureCollection<Geometry>,
  segmentLengthInKm: number = 30,
  bufferInKm: number = 2
): FeatureCollection<Polygon> => {
  const finalPolygons: Feature<Polygon>[] = [];

  for (const feature of geoJsonPaths.features) {
    const { geometry } = feature;

    const processLine = (coords: number[][]) => {
      if (coords.length < 2) return;

      const line: Feature<LineString> = {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: coords,
        },
        properties: {},
      };

      const chunks = lineChunk(line, segmentLengthInKm, { units: "kilometers" });

      for (const chunk of chunks.features) {
        if (chunk.geometry.coordinates.length < 2) continue;

        try {
          const buffered = buffer(chunk, bufferInKm, { units: "kilometers" }) as Feature<Polygon | MultiPolygon>;
          const potentialPolygons: Feature<Polygon>[] = [];

          // Handle both Polygon and MultiPolygon results from buffer
          if (buffered.geometry.type === "Polygon") {
            potentialPolygons.push(buffered as Feature<Polygon>);
          } else if (buffered.geometry.type === "MultiPolygon") {
            buffered.geometry.coordinates.forEach((polyCoords) => {
              potentialPolygons.push(polygon(polyCoords, buffered.properties));
            });
          }

          // Process each potential polygon
          for (const currentPoly of potentialPolygons) {
            let cleaned: Feature<Polygon> | null = simplifyToMaxPoints(
              currentPoly,
              50,
              [0.005, 0.05]
            ) as Feature<Polygon>;

            // Subtract overlapping areas from previously added polygons
            for (const existing of finalPolygons) {
              if (!cleaned) break; // Skip if already nullified
              const expanded = buffer(existing, 0.000001, { units: "degrees" }); // Use degrees for very small buffer to help avoid micro overlaps
              const diff = ensurePolygon(difference(cleaned, expanded));
              if (diff) {
                cleaned = diff as Feature<Polygon>;
              } else {
                // If fully overlapped or invalid result, skip this polygon
                cleaned = null;
              }
            }

            if (cleaned) {
              finalPolygons.push(cleaned);
            }
          }
        } catch (err) {
          console.warn("Buffer or difference operation failed on chunk:", err);
        }
      }
    };

    if (geometry.type === "LineString") {
      processLine(geometry.coordinates as number[][]);
    }

    if (geometry.type === "MultiLineString") {
      for (const coords of geometry.coordinates as number[][][]) {
        processLine(coords);
      }
    }
  }

  return featureCollection(finalPolygons);
};

export {
  pathToBufferedPolygon,
  getInterpolatedTimeFromLines,
  getTotalArea,
  diffAreaObject,
  combineFeaturesIntoMultiPolygon,
  filterAreasListByGroupId,
  filterAreasListByName,
  buildAreasUrl,
  getLayerColor,
  getLayerBorderColor,
  getFeaturesTotalArea,
  createPolygonWithMask,
  selfIntersectingFeatureCollection,
  getBBoxFromZoomAndCenter,
  getBBoxFromPoints,
  resamplePath,
  isValidLatLng,
};
